import { AppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { getClientSideQueryClient } from "@modules/api/queryClients";
import { getClientAppInsights } from "@modules/core/applicationInsights/clientTelemetryService";
import { User } from "@modules/user/types/user";
import userQueryKeys from "@modules/user/userQueryKeys";
import { captureException, ErrorBoundary } from "@sentry/nextjs";
import "@styles/globals.css";
import {
  DehydratedState,
  HydrationBoundary,
  QueryClientProvider,
} from "@tanstack/react-query";
import { NextPage } from "next";
import { appWithTranslation } from "next-i18next";
import { AppProps } from "next/app";
import { ReactElement, ReactNode, useEffect, useState } from "react";
import { IPageViewTelemetry } from "@microsoft/applicationinsights-web";
import { Mulish } from "next/font/google";
import { ErrorFallback } from "@talladega/components";
import usePreserveQueryParams from "@common/hooks/usePreserveQueryParams";
import nextI18NextConfig from "../../next-i18next.config";

const mulish = Mulish({ subsets: ["latin"] });

type PageProps = {
  dehydratedState: DehydratedState;
};

type NextPageWithOptions = NextPage<{ dehydratedState: DehydratedState }> & {
  pageName: string;
  getLayout?: (page: ReactElement) => ReactNode;
  pageWrapper?: (page: ReactElement) => ReactElement;
};

type AppPropsWithOptions = AppProps & {
  Component: NextPageWithOptions;
  pageProps: PageProps;
};

const noOpWrapper = (page: ReactElement) => {
  return page;
};

const errorFallbackPage = () => <ErrorFallback />;

const MyApp = ({
  Component,
  pageProps,
  router,
}: AppPropsWithOptions): JSX.Element => {
  const {
    clientAppInsights,
    initialize: initializeAppInsights,
    reactPlugin,
  } = getClientAppInsights();
  const { pageName } = Component;

  const [queryClient] = useState(() => getClientSideQueryClient());

  if (!pageName) {
    throw new Error("Component's `pageName` property is not set");
  }

  useEffect(() => {
    initializeAppInsights();
  }, [initializeAppInsights]);

  useEffect(() => {
    const { route, query } = router;

    const properties: Record<string, string | string[] | undefined> = {
      route,
    };

    if (query) {
      Object.keys(query).forEach((key: string) => {
        properties[`query.${key}`] = query[key];
      });
    }

    const pageView: IPageViewTelemetry = {
      name: pageName,
      properties,
    };

    clientAppInsights?.trackPageView(pageView);
  }, [clientAppInsights, pageName, router]);

  usePreserveQueryParams(["/quote"]);

  const getLayout = Component.getLayout ?? noOpWrapper;
  const getWrapper = Component.pageWrapper ?? noOpWrapper;

  const getLoggedInUserId = (): string | null => {
    let LoggedInUserId = null;
    const user = queryClient.getQueryData(userQueryKeys.user()) as User;

    if (user && user.id) {
      LoggedInUserId = user.id;
    }
    return LoggedInUserId;
  };

  return (
    <AppInsightsContext.Provider value={reactPlugin}>
      <QueryClientProvider client={queryClient}>
        <HydrationBoundary state={pageProps.dehydratedState}>
          <ErrorBoundary
            beforeCapture={(scope) => {
              const loggedInUserId = getLoggedInUserId();

              if (loggedInUserId) {
                scope.setUser({ id: loggedInUserId });
              }
            }}
            onError={(error) => {
              captureException(error);
            }}
            fallback={errorFallbackPage}
          >
            <>
              <style jsx global>{`
                :root {
                  --font-mulish: ${mulish.style.fontFamily};
                }
              `}</style>
              {getLayout(getWrapper(<Component {...pageProps} />))}
            </>
          </ErrorBoundary>
        </HydrationBoundary>
      </QueryClientProvider>
    </AppInsightsContext.Provider>
  );
};

export default appWithTranslation(MyApp, nextI18NextConfig);
